import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { COA_TEMPLATES, type CoaTemplateId } from '@finvox/shared';
import { choiceCardVariants } from '@/components/ui/button-variants';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { useAuth } from '@/contexts/AuthContext';
import { useOrg } from '@/contexts/OrgContext';
import { createOrganization } from '@/services/createOrganization';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useToast } from '@/contexts/ToastContext';
import { getOnboardingErrorKey } from '@/lib/friendlyError';
import { FIRESTORE_OP_TIMEOUT_MS, withTimeout } from '@/lib/withTimeout';

const TEMPLATES: CoaTemplateId[] = ['eg', 'sa', 'gcc', 'generic'];

export function OnboardingPage() {
  const { t } = useTranslation();
  const { user, signOutUser } = useAuth();
  const { refreshOrg } = useOrg();
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const toast = useToast();
  const [name, setName] = useState('');
  const [template, setTemplate] = useState<CoaTemplateId>('eg');
  const [busy, setBusy] = useState(false);

  async function handleCreate() {
    if (!user || !online || !name.trim()) return;
    setBusy(true);
    try {
      await withTimeout(
        createOrganization(user.uid, user.email ?? '', name.trim(), template),
        FIRESTORE_OP_TIMEOUT_MS,
        'createOrganization'
      );
      await withTimeout(refreshOrg({ silent: true }), FIRESTORE_OP_TIMEOUT_MS, 'refreshOrg');
      navigate('/', { replace: true });
    } catch (e) {
      if (import.meta.env.DEV) console.error('[Onboarding] createOrganization failed', e);
      toast.error(t(getOnboardingErrorKey('createOrganization', e)), 'onboarding');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg p-6">
      <h1 className="text-2xl font-bold">{t('onboarding.title')}</h1>
      <p className="mt-2 text-sm text-[var(--fv-text-muted)]">{t('onboarding.subtitle')}</p>
      <label className="mt-6 block text-sm font-medium">
        {t('onboarding.orgName')}
        <input
          className="mt-1 w-full rounded-[var(--fv-radius)] border border-[var(--fv-border)] px-3 py-2"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!online || busy}
        />
      </label>
      <fieldset className="mt-4">
        <legend className="text-sm font-medium">{t('onboarding.template')}</legend>
        <div className="mt-2 grid gap-2">
          {TEMPLATES.map((id) => (
            <label
              key={id}
              className={cn(
                choiceCardVariants({ selected: template === id }),
                'flex items-center gap-3'
              )}
            >
              <input
                type="radio"
                name="tpl"
                className="sr-only"
                checked={template === id}
                onChange={() => setTemplate(id)}
              />
              <span>
                {COA_TEMPLATES[id].label} ({COA_TEMPLATES[id].accounts.length}{' '}
                {t('onboarding.accounts')})
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <Button
        className="mt-6"
        loading={busy}
        disabled={!online || !name.trim()}
        onClick={() => void handleCreate()}
      >
        {t('onboarding.create')}
      </Button>

      <div className="mt-10 border-t border-[var(--fv-border)] pt-6 text-center">
        <Button type="button" variant="link" onClick={() => void signOutUser()}>
          {t('auth.signOut')}
        </Button>
      </div>
    </div>
  );
}
